import { useSyncExternalStore } from "react";

import type { ContextualAsk } from "../chat/AskContext";
import { resetChat } from "../chat/chatStore";
import type { ScopeTouched, WatchlistScope } from "./types";

const STORAGE_KEY = "clank.watchlist.recent-questions.v1";
const RECENT_QUESTION_LIMIT = 3;
const TITLE_LIMIT = 72;

export type RecentQuestion = {
  id: string;
  title: string;
  scope: WatchlistScope;
  /** Which parts of the saved context were deliberately selected. */
  scopeTouched?: ScopeTouched;
  scopeLabel: string;
  alertId?: string;
  context?: ContextualAsk;
  updatedAt: string;
};

export const questionChatKey = (id: string): string => `watchlist-question:${id}`;

const titleFrom = (question: string): string => {
  const title = question.trim();
  const characters = Array.from(title);
  if (characters.length <= TITLE_LIMIT) return title;
  return `${characters
    .slice(0, TITLE_LIMIT - 1)
    .join("")
    .trimEnd()}…`;
};

const restore = (): RecentQuestion[] => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const stored = JSON.parse(raw) as RecentQuestion[];
    if (!Array.isArray(stored)) return [];
    return stored.slice(0, RECENT_QUESTION_LIMIT);
  } catch {
    return [];
  }
};

let recentQuestions = restore();
const listeners = new Set<() => void>();

const emit = () => {
  for (const listener of listeners) listener();
};

const persist = () => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(recentQuestions));
  } catch {
    // The conversations still work in memory when session storage is unavailable.
  }
};

const replace = (next: RecentQuestion[]) => {
  const kept = next.slice(0, RECENT_QUESTION_LIMIT);
  const keptIds = new Set(kept.map(question => question.id));
  for (const question of recentQuestions) {
    if (!keptIds.has(question.id)) resetChat(questionChatKey(question.id));
  }
  recentQuestions = kept;
  persist();
  emit();
};

export const addRecentQuestion = (
  question: string,
  scope: WatchlistScope,
  scopeLabel: string,
  scopeTouched?: ScopeTouched,
  context?: ContextualAsk,
  existingId?: string,
): RecentQuestion => {
  const id =
    existingId ??
    window.crypto.randomUUID?.() ??
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  const recent = {
    id,
    title: titleFrom(question),
    scope,
    scopeTouched,
    context,
    scopeLabel,
    updatedAt: new Date().toISOString(),
  };
  replace([recent, ...recentQuestions.filter(item => item.id !== id)]);
  return recent;
};

export const touchRecentQuestion = (
  id: string,
  scope: WatchlistScope,
  scopeLabel: string,
  scopeTouched?: ScopeTouched,
  context?: ContextualAsk,
): void => {
  const current = recentQuestions.find(question => question.id === id);
  if (!current) return;
  replace([
    { ...current, scope, scopeTouched, scopeLabel, context, updatedAt: new Date().toISOString() },
    ...recentQuestions.filter(question => question.id !== id),
  ]);
};

/** Keep the scheduled alert attached to the conversation that created it. */
export const attachQuestionAlert = (questionId: string, alertId: string): void => {
  replace(
    recentQuestions.map(question =>
      question.id === questionId ? { ...question, alertId } : question,
    ),
  );
};

export type RemovedRecentQuestion = {
  question: RecentQuestion;
  index: number;
};

/** Removes a question from the recent list without destroying its chat yet. */
export const removeRecentQuestion = (id: string): RemovedRecentQuestion | null => {
  const index = recentQuestions.findIndex(question => question.id === id);
  if (index < 0) return null;
  const question = recentQuestions[index];
  recentQuestions = recentQuestions.filter(candidate => candidate.id !== id);
  persist();
  emit();
  return { question, index };
};

/** Puts a recently removed question back in its original position. */
export const restoreRecentQuestion = ({ question, index }: RemovedRecentQuestion): void => {
  if (recentQuestions.some(candidate => candidate.id === question.id)) return;
  const next = [...recentQuestions];
  next.splice(Math.min(index, next.length), 0, question);
  recentQuestions = next.slice(0, RECENT_QUESTION_LIMIT);
  persist();
  emit();
};

/** Permanently destroys the transcript after the undo window has elapsed. */
export const deleteRecentQuestionChat = (id: string): void => {
  resetChat(questionChatKey(id));
};

export const useRecentQuestions = (): RecentQuestion[] =>
  useSyncExternalStore(
    callback => {
      listeners.add(callback);
      return () => listeners.delete(callback);
    },
    () => recentQuestions,
  );

/** Non-reactive snapshot for callers that only need the current value. */
export const recentQuestionsSnapshot = (): RecentQuestion[] => recentQuestions;
